import type { AxiosInstance } from 'axios'
import type { ServiceproviderCard } from '~~/types/admin.d'
import type { ProjectCard } from '~~/types/other'

// returns null when the API responds with 204 (no picture set); still throws
// on actual request errors (network failure, 5xx, ...)
export async function getBufferPictureToBase64 (
  axios: AxiosInstance,
  url: string
): Promise<string | null> {
  const res = await axios.get(url, {
    responseType: 'arraybuffer',
    validateStatus: status => status === 200 || status === 204
  })

  if (res.status !== 200 || !res.data || res.data.byteLength === 0) {
    return null
  }

  // Convert the ArrayBuffer to a binary string
  const binary = Array.from(new Uint8Array(res.data)).map(byte =>
    String.fromCharCode(byte)
  ).join('')
  // Encode the binary string to base64
  const base64 = btoa(binary)
  return 'data:' + res.headers['content-type'] + ';base64,' + base64
}

export function blobToFile (theBlob: Blob, fileName: string): File {
  return new File([theBlob], fileName, {
    type: theBlob.type
  })
}

export function securityScoreToColor (score: number, asTextClass = false): string {
  let color

  if (score >= 85) {
    color = 'special-green-text'
  } else if (score >= 70) {
    color = 'special-yellow-text'
  } else if (score >= 0) {
    color = 'special-red-text'
  } else {
    color = 'secondary-dark'
  }
  if (asTextClass) {
    return 'text-' + color
  }

  return 'bg-' + color
}

interface RawProjectCardData {
  client_id_pk?: string
  client_name?: string
  serviceprovider_name?: string
  client_address?: string
  client_security_score?: number
  client_subscription_end?: string
  client_reported_employee_count?: number
  client_billing_cycle?: string
}

export function createEnrichedProjectCard (rawData: RawProjectCardData[]): ProjectCard[] {
  return rawData.map((item) => {
    return {
      id: item?.client_id_pk,
      type: 'client',
      title: item?.client_name || item?.serviceprovider_name,
      clientAddress: item?.client_address,
      initials: item?.client_name?.substring(0, 2),
      securityScore: item?.client_security_score || -1,
      bgColor: 'bg-secondary-dark',
      subscriptionEnd: item?.client_subscription_end,
      employeeCount: item?.client_reported_employee_count,
      cycle: item?.client_billing_cycle
    }
  })
}

interface RawServiceproviderCardData {
  serviceprovider_id_pk?: number
  serviceprovider_name?: string
  serviceprovider_address?: string
  serviceprovider_verified?: boolean
  audit: { aggregate?: { count?: number } }
  monitoring: { aggregate?: { count?: number } }
  monitoringNfr: { aggregate?: { count?: number } }
  quickcheck: { aggregate?: { count?: number } }
}

export function createEnrichedServiceproviderProjectCard (rawData: RawServiceproviderCardData[]): ServiceproviderCard[] {
  return rawData.map((item) => {
    const isServiceprovider = !!item?.serviceprovider_id_pk
    const isVerified = !isServiceprovider || item?.serviceprovider_verified === true
    return {
      id: item?.serviceprovider_id_pk,
      type: 'serviceprovider',
      title: item?.serviceprovider_name,
      clientAddress: item?.serviceprovider_address,
      initials: item?.serviceprovider_name?.substring(0, 2),
      bgColor: isVerified ? 'bg-red-light' : 'bg-secondary-dark',
      auditCount: item?.audit.aggregate?.count || 0,
      monitoringCount: item?.monitoring.aggregate?.count || 0,
      monitoringNfrCount: item?.monitoringNfr.aggregate?.count || 0,
      quickcheckCount: item?.quickcheck.aggregate?.count || 0
    }
  })
}

// removes all extra roles to return one of the three main roles
// client, serviceprovider or admin
export function getMainRole (roles: string[] | undefined): string {
  if (!roles) {
    return ''
  }

  const rolesCopy = [...roles]

  // remove allow_next_usage
  const nextUserIndex = rolesCopy.indexOf('next_user')

  if (nextUserIndex > -1) {
    rolesCopy.splice(nextUserIndex, 1)
  }

  // remove allow_demo_age
  const demoUserIndex = rolesCopy.indexOf('demo_user')

  if (demoUserIndex > -1) {
    rolesCopy.splice(demoUserIndex, 1)
  }

  return rolesCopy?.[0] || ''
}

export class AppException extends Error {
  private code: string

  constructor (code: string, message: unknown) {
    const fullMsg = message ? `${code}: ${message}` : code
    super(fullMsg)
    this.name = code
    this.code = code
    this.message = fullMsg
  }

  toString (): string {
    return this.message
  }
}

export function getLetterGrade (score: number): string {
  if (score >= 99) {
    return 'A+'
  } else if (score >= 97) {
    return 'A'
  } else if (score >= 95) {
    return 'A-'
  } else if (score >= 90) {
    return 'B+'
  } else if (score >= 85) {
    return 'B'
  } else if (score >= 80) {
    return 'B-'
  } else if (score >= 75) {
    return 'C+'
  } else if (score >= 70) {
    return 'C'
  } else if (score >= 65) {
    return 'C-'
  } else if (score >= 60) {
    return 'D+'
  } else if (score >= 55) {
    return 'D'
  } else if (score >= 50) {
    return 'D-'
  } else if (score >= 0) {
    return 'F'
  } else {
    return '-'
  }
}

// deep merge to objeects
export function deepMerge<T extends Record<string, unknown> = Record<string, unknown>> (...objects: Array<Partial<T> | null | undefined>): T {
  // create a new object
  const target: Record<string, unknown> = {}

  // helper function to check if value is a plain object
  const isPlainObject = (obj: unknown): boolean => {
    return Object.prototype.toString.call(obj) === '[object Object]'
  }

  // helper function to check if value is an array
  const isArray = (obj: unknown): boolean => {
    return Array.isArray(obj)
  }

  // deep merge the object into the target object
  const merger = (obj: Record<string, unknown>): void => {
    for (const prop in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, prop)) {
        const objValue = obj[prop]
        const targetValue = target[prop]

        if (isArray(objValue)) {
          if (isArray(targetValue)) {
            // Both are arrays - merge array elements
            target[prop] = mergeArrays(targetValue as unknown[], objValue as unknown[])
          } else {
            // Target is not an array, use the new array
            target[prop] = (objValue as unknown[]).map((item: unknown) =>
              isPlainObject(item) ? deepMerge({}, item as Record<string, unknown>) : item
            )
          }
        } else if (isPlainObject(objValue)) {
          if (isPlainObject(targetValue)) {
            // Both are objects - recursively merge
            target[prop] = deepMerge(targetValue as Record<string, unknown>, objValue as Record<string, unknown>)
          } else {
            // Target is not an object, use the new object
            target[prop] = deepMerge({}, objValue as Record<string, unknown>)
          }
        } else {
          // for regular property
          target[prop] = objValue
        }
      }
    }
  }

  // helper function to merge arrays
  const mergeArrays = (targetArray: unknown[], sourceArray: unknown[]): unknown[] => {
    const result = [...targetArray]

    for (let i = 0; i < sourceArray.length; i++) {
      if (i < result.length) {
        const targetItem = result[i]
        const sourceItem = sourceArray[i]

        if (isPlainObject(targetItem) && isPlainObject(sourceItem)) {
          // Both are objects - merge them
          result[i] = deepMerge(targetItem as Record<string, unknown>, sourceItem as Record<string, unknown>)
        } else {
          // Replace with source item
          result[i] = isPlainObject(sourceItem) ? deepMerge({}, sourceItem as Record<string, unknown>) : sourceItem
        }
      } else {
        // Add new item from source
        result[i] = isPlainObject(sourceArray[i]) ? deepMerge({}, sourceArray[i] as Record<string, unknown>) : sourceArray[i]
      }
    }

    return result
  }

  // iterate through all objects and
  // deep merge them with target
  for (let i = 0; i < objects.length; i++) {
    if (objects[i] != null) {
      merger(objects[i] as Record<string, unknown>)
    }
  }

  return target as T
}

export function convertUUIDForPostgresSchema (uuid: string): string {
  return uuid.replace(/-/g, '_')
}

export function convertPostgresSchemaToUUID (schema: string): string {
  return schema.replace(/_/g, '-')
}

export function downloadCSV (content: BlobPart, filename: string): void {
  const blob = new Blob([content], { type: 'text/csv' })
  const url = window.URL.createObjectURL(blob)

  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  link.remove()
}

export const employeePricingRanges = [
  { range: '1 - 9 Mitarbeitende', quickcheck: 'Kostenlos', audit: '49 €', monthly: '5 €', yearly: '55 €', low: 1, high: 9 },
  { range: '10 - 24 Mitarbeitende', quickcheck: 'Kostenlos', audit: '49 €', monthly: '10 €', yearly: '110 €', low: 10, high: 24 },
  { range: '25 - 49 Mitarbeitende', quickcheck: 'Kostenlos', audit: '49 €', monthly: '25 €', yearly: '275 €', low: 25, high: 49 },
  { range: '50 - 99 Mitarbeitende', quickcheck: 'Kostenlos', audit: '49 €', monthly: '50 €', yearly: '550 €', low: 50, high: 99 },
  { range: '100 - 199 Mitarbeitende', quickcheck: 'Kostenlos', audit: '49 €', monthly: '75 €', yearly: '825 €', low: 100, high: 199 },
  { range: '200 - 349 Mitarbeitende', quickcheck: 'Kostenlos', audit: '49 €', monthly: '150 €', yearly: '1650 €', low: 200, high: 349 },
  { range: '350 - 499 Mitarbeitende', quickcheck: 'Kostenlos', audit: '49 €', monthly: '225 €', yearly: '2475 €', low: 350, high: 499 },
  { range: '500 - 699 Mitarbeitende', quickcheck: 'Kostenlos', audit: '49 €', monthly: '300 €', yearly: '3300 €', low: 500, high: 699 },
  { range: '700 - 849 Mitarbeitende', quickcheck: 'Kostenlos', audit: '49 €', monthly: '375 €', yearly: '4125 €', low: 700, high: 849 },
  { range: '850 - 1000 Mitarbeitende', quickcheck: 'Kostenlos', audit: '49 €', monthly: '450 €', yearly: '4950 €', low: 850, high: 1000 },
  { range: '1000+ Mitarbeitende', quickcheck: 'Kostenlos', audit: '49 €', monthly: 'auf Anfrage', yearly: 'auf Anfrage', low: 1001, high: 999999 }
] as const

export const userPricingRanges = [
  { range: '1 - 24', monitoring: '0,57 €', audit: '49 €', quickcheck: 'kostenlos', low: 1, high: 24 },
  { range: '25 - 99', monitoring: '0,54 €', audit: '49 €', quickcheck: 'kostenlos', low: 25, high: 99 },
  { range: '100 - 199', monitoring: '0,51 €', audit: '49 €', quickcheck: 'kostenlos', low: 100, high: 199 },
  { range: '200 - 349', monitoring: '0,48 €', audit: '49 €', quickcheck: 'kostenlos', low: 200, high: 349 },
  { range: '350 - 599', monitoring: '0,45 €', audit: '49 €', quickcheck: 'kostenlos', low: 350, high: 599 },
  { range: '600 - 1500', monitoring: '0,42 €', audit: '49 €', quickcheck: 'kostenlos', low: 600, high: 1500 },
  { range: '1500+', monitoring: 'Auf Anfrage', audit: '49 €', quickcheck: 'kostenlos', low: 1501, high: 999999 }
] as const
